import React, { useMemo } from "react";
import { useEscapeKey } from "../../hooks/useEscapeKey";
import { Info, X } from "lucide-react";
import { formatModifierKey } from "../../lib/platform";
import { shortcutSections } from "../../lib/shortcuts";
import type { ViewType } from "../../types";
import ShortcutDescription from "../shortcuts/ShortcutDescription";
// Hidden until the underlying ⌘+/ overlay feature is fully shipped. Keep
// the import + usage commented (not deleted) — the banner's intro animation
// is intentional and worth preserving for when this re-enables.
// import ShortcutHintBanner from "../shortcuts/ShortcutHintBanner";

interface KeyboardShortcutsDialogProps {
  onClose: () => void;
  /**
   * The view the user is currently on. When provided, the dialog shows only
   * the global section and the section matching that view, keeping the list
   * short and relevant. Pass `null` (or omit) to render every section in
   * default order — used by Settings → Keyboard Shortcuts.
   */
  currentView?: ViewType | null;
  /**
   * When true, the task-dialog shortcuts (Next / Previous task) are added
   * to the list because they actually do something right now. Triggered by
   * the parent whenever the task edit dialog is open underneath.
   */
  taskDialogOpen?: boolean;
}

// Left padding for nested rows / sub-headings. Static classes so Tailwind
// keeps them; level 2 ("Filter (cycle)" group) sits deeper than level 1.
const indentClass = (level?: number) =>
  level === 2 ? "pl-6" : level === 1 ? "pl-3" : "";

// The kbd caps live in a right-aligned auto-width column, so padding on the
// cell is absorbed by the column instead of shifting them. A transform moves
// the cap visually (matched to the label's padding) without resizing the
// column, so the whole nested row shifts right together.
const kbdIndentClass = (level?: number) =>
  level === 2 ? "translate-x-6" : level === 1 ? "translate-x-3" : "";

const Kbd: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <kbd className="inline-flex items-center justify-center min-w-[24px] h-6 px-1.5 bg-warm-bg-base border border-warm-border rounded text-xs font-mono text-warm-text-primary shadow-sm">
    {children}
  </kbd>
);

const KeyboardShortcutsDialog: React.FC<KeyboardShortcutsDialogProps> = ({
  onClose,
  currentView = null,
  taskDialogOpen = false,
}) => {
  useEscapeKey(onClose);

  const visibleSections = useMemo(() => {
    if (currentView === null) return shortcutSections;
    // The contextual ? dialog stays focused on what works right now.
    // When a task edit dialog is open underneath, the board and global
    // shortcuts are all suppressed (they require no dialog open), so only
    // the task-dialog shortcuts actually fire — show just that section.
    // Otherwise show the global section plus the section for the active
    // view. Settings → Keyboard Shortcuts (currentView=null) lists every
    // section regardless.
    if (taskDialogOpen) {
      return shortcutSections.filter((s) => s.id === "task-dialog");
    }
    return shortcutSections.filter(
      (s) => s.id === "global" || s.view === currentView,
    );
  }, [currentView, taskDialogOpen]);

  // The two-column wide layout is built for a dense, multi-section list. When
  // only one short section is showing (the task-open case), it leaves the whole
  // right column empty in an oversized panel, so size down to a single,
  // narrower column instead.
  const isCompact = visibleSections.length === 1;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm animate-dialog-backdrop" />
      <div
        role="dialog"
        aria-label="Keyboard Shortcuts"
        className={`relative bg-warm-bg-card rounded-xl shadow-soft-lg w-full ${
          isCompact ? "max-w-md" : "max-w-3xl"
        } max-h-[95vh] sm:max-h-[85vh] overflow-y-auto animate-dialog-panel`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b border-warm-border">
          <h2 className="text-lg font-semibold text-warm-text-primary">
            Keyboard Shortcuts
          </h2>
          <button
            onClick={onClose}
            className="p-1.5 text-warm-text-secondary hover:text-warm-text-primary hover:bg-warm-bg-elevated rounded-soft transition-all"
          >
            <X size={18} />
          </button>
        </div>

        <div className="px-4 sm:px-6 py-4 space-y-4">
          {/* <ShortcutHintBanner /> — hidden until ⌘+/ overlay ships */}

          {currentView !== null && (
            <p className="flex items-start gap-2 text-xs text-warm-text-tertiary leading-snug">
              <Info size={13} className="flex-shrink-0 mt-0.5" />
              <span>
                This is a smart dialog. It only shows the shortcuts relevant to
                your{" "}
                <strong className="text-warm-text-secondary font-semibold">
                  current screen
                </strong>
                , so open it from a different screen to see the shortcuts
                available there.
              </span>
            </p>
          )}

          <div
            className={`grid ${
              isCompact ? "grid-cols-1" : "grid-cols-1 sm:grid-cols-2"
            } gap-x-8 gap-y-6 pt-2`}
          >
            {visibleSections.map((section) => {
              const isCurrentView =
                currentView !== null && section.view === currentView;
              return (
                <section key={section.id}>
                  <h3 className="font-heading text-base font-semibold text-warm-text-primary mb-3 pb-1.5 border-b border-warm-border flex items-center gap-2">
                    <span>{section.title}</span>
                    {isCurrentView && (
                      <span className="text-[10px] font-medium normal-case tracking-normal px-1.5 py-0.5 rounded-full bg-warm-bg-elevated text-warm-text-secondary">
                        Current view
                      </span>
                    )}
                  </h3>
                <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-2 items-center">
                  {section.shortcuts.map((shortcut, i) => {
                    const next = section.shortcuts[i + 1];
                    // Last row in a sub-group is followed by an ungrouped row
                    // (no indent); pad it so the eye registers the group
                    // ending. When the next row starts a new sub-group, the
                    // sub-heading's own top padding handles the gap.
                    const closesSubgroup =
                      shortcut.indentLevel && next && !next.indentLevel;
                    const trailingSpaceClass = closesSubgroup ? "pb-3" : "";
                    return (
                      <React.Fragment key={i}>
                        {shortcut.subheadingBefore && (
                          <div
                            className={`col-span-2 text-xs font-bold uppercase tracking-wider text-warm-text-secondary pb-1 ${
                              i === 0 ? "" : "pt-3"
                            } ${indentClass(shortcut.subheadingIndentLevel)}`}
                          >
                            {shortcut.subheadingBefore}
                          </div>
                        )}
                        <div
                          className={`flex items-center gap-1 justify-end ${kbdIndentClass(
                            shortcut.indentLevel,
                          )} ${trailingSpaceClass}`}
                        >
                          {shortcut.keys.map((key, j) => (
                            <React.Fragment key={j}>
                              {j > 0 && !shortcut.keysAreAlternatives && (
                                <span className="text-xs text-warm-text-secondary">
                                  {shortcut.chord ? "then" : "+"}
                                </span>
                              )}
                              <Kbd>{formatModifierKey(key)}</Kbd>
                            </React.Fragment>
                          ))}
                        </div>
                        <span
                          className={`text-sm text-warm-text-primary ${indentClass(
                            shortcut.indentLevel,
                          )} ${trailingSpaceClass}`}
                        >
                          <ShortcutDescription text={shortcut.description} />
                        </span>
                      </React.Fragment>
                    );
                  })}
                </div>
                {section.note && (
                  <p className="text-xs text-warm-text-tertiary leading-snug mt-3">
                    {section.note}
                  </p>
                )}
              </section>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default KeyboardShortcutsDialog;
